import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import classnames from 'classnames';
import onClickOutside from 'react-onclickoutside';
import countryData from 'country-telephone-data';
import { format } from '@breezeway/brzw-react-core';
import * as utils from './utils';
import './international_phone.scss';

const { allCountries } = countryData;
const iso2Lookup = _.omit(countryData.iso2Lookup, ['io', 'fk', 'gf', 'nf', 'mp', 'pm', 'tk', 'wf']);

class ReactTelephoneInput extends Component {
  constructor(props) {
    super(props);

    const preferredCountries = _.filter(
      _.map(
        this.props.preferredCountries,
        iso2 => _.has(iso2Lookup, iso2) && allCountries[iso2Lookup[iso2]],
      ),
    );

    const searchCountries = _.filter(
      _.map(
        this.props.onlyCountries,
        iso2 => _.has(iso2Lookup, iso2) && allCountries[iso2Lookup[iso2]],
      ),
    );

    this.preferredCountries = preferredCountries;
    this.searchCountries = searchCountries;
    this.optionCountries = _.concat(preferredCountries, searchCountries);

    this.flagRefs = {};

    const highlightCountryIndex = _.has(iso2Lookup, this.props.defaultCountry)
      ? iso2Lookup[this.props.defaultCountry]
      : 0;
    const selectedCountry = allCountries[highlightCountryIndex];
    const formattedNumber = format.phone.formatNumber(
      `${selectedCountry.dialCode}${this.props.value.replace(/\D/g, '')}`,
      selectedCountry,
    );

    this.state = {
      showDropDown: false,
      queryString: '',
      freezeSelection: false,
      debouncedQueryStingSearcher: _.debounce(this.searchCountry, 300),
      selectedCountry,
      highlightCountryIndex,
      formattedNumber,
    };

    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.handleFlagDropdownClick = this.handleFlagDropdownClick.bind(this);
    this.handleFlagItemClick = this.handleFlagItemClick.bind(this);
    this.handleInput = this.handleInput.bind(this);
    this.handleInputBlur = this.handleInputBlur.bind(this);
    this.handleInputClick = this.handleInputClick.bind(this);
    this.handleInputFocus = this.handleInputFocus.bind(this);
    this.handleInputKeyDown = this.handleInputKeyDown.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  componentDidMount() {
    document.addEventListener('keydown', this.handleKeyDown);

    this.cursorToEnd(true);
    if (typeof this.props.onChange === 'function') {
      this.props.onChange(
        this.state.formattedNumber,
        this.state.selectedCountry.iso2,
        this.state.selectedCountry.dialCode,
      );
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return !_.isEqual(nextProps, this.props) || !_.isEqual(nextState, this.state);
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown);
  }

  getCountryDropDownList() {
    const countryDropDownList = _.map(this.optionCountries, (country, index) => {
      const itemClasses = classnames({
        country: true,
        preferred: _.findIndex(this.preferredCountries, country.iso2) >= 0,
        highlight: this.state.highlightCountryIndex === index,
      });

      const inputFlagClasses = `flag ${country.iso2}`;

      return (
        <li
          key={`flag_no_${index}`}
          ref={wrapper => {
            this.flagRefs[index] = wrapper;
          }}
          data-flag-key={`flag_no_${index}`}
          className={itemClasses}
          data-dial-code="1"
          data-country-code={country.iso2}
          onClick={e => this.handleFlagItemClick(country, e)}
          role="menuitem"
          tabIndex={index}
        >
          <div className={inputFlagClasses} />
          <span className="country-name">{country.name}</span>
          <span className="dial-code">+{country.dialCode}</span>
        </li>
      );
    });

    const dashedLi = <li key="dashes" className="divider" />;
    // let's insert a dashed line in between preferred countries and the rest
    countryDropDownList.splice(this.preferredCountries.length, 0, dashedLi);

    const dropDownClasses = classnames({
      'country-list': true,
      hide: !this.state.showDropDown,
    });
    return (
      <ul
        className={dropDownClasses}
        ref={wrapper => {
          this.flagDropdownListWrapper = wrapper;
        }}
      >
        {countryDropDownList}
      </ul>
    );
  }

  getElement(index) {
    return this.flagRefs[index];
  }

  getHighlightCountryIndex(direction) {
    // had to write own function because underscore does not have findIndex. lodash has it
    const highlightCountryIndex = this.state.highlightCountryIndex + direction;

    if (highlightCountryIndex < 0 || highlightCountryIndex >= this.optionCountries.length) {
      return highlightCountryIndex - direction;
    }

    return highlightCountryIndex;
  }

  cursorToEnd(skipFocus) {
    const input = this.phoneInputWrapper;
    if (skipFocus) {
      this.fillDialCode();
    } else {
      input.focus();

      if (utils.isModernBrowser) {
        const len = input.value.length;
        input.setSelectionRange(len, len);
      }
    }
  }

  fillDialCode() {
    // if the input is blank, insert dial code of the selected country
    if (this.phoneInputWrapper.value === '+') {
      this.setState({ formattedNumber: `+${this.state.selectedCountry.dialCode}` });
    }
  }

  handleClickOutside() {
    if (this.state.showDropDown) {
      this.setState({ showDropDown: false });
    }
  }

  handleFlagItemClick(selectedCountry, event) {
    _.invoke(event, 'preventDefault');
    _.invoke(event, 'stopPropagation');

    // tiny optimization
    if (this.state.selectedCountry.iso2 !== selectedCountry.iso2) {
      const newNumber = format.phone.replaceCountryCode(
        this.state.selectedCountry,
        selectedCountry,
        this.state.formattedNumber.replace(/\D/g, ''),
      );

      const formattedNumber = format.phone.formatNumber(newNumber, selectedCountry);

      this.setState(
        {
          showDropDown: false,
          selectedCountry,
          freezeSelection: true,
          formattedNumber,
        },
        () => {
          this.cursorToEnd();
          if (this.props.onChange) {
            this.props.onChange(formattedNumber, selectedCountry.iso2, selectedCountry.dialCode);
          }
        },
      );
    } else {
      this.setState({ showDropDown: false });
    }
  }

  handleFlagDropdownClick(e) {
    if (this.props.disabled) {
      return;
    }

    _.invoke(e, 'preventDefault');
    // need to put the highlight on the current selected country if the dropdown is going to open up
    this.setState(
      {
        showDropDown: !this.state.showDropDown,
        highlightCountryIndex: _.findIndex(this.optionCountries, this.state.selectedCountry.iso2),
      },
      () => {
        // only need to scroll if the dropdown list is alive
        if (this.state.showDropDown) {
          this.scrollTo(
            this.getElement(this.state.highlightCountryIndex + this.preferredCountries.length),
          );
        }
      },
    );
  }

  handleInput(event) {
    let formattedNumber = '+';
    let newSelectedCountry = this.state.selectedCountry;
    let { freezeSelection } = this.state;

    // if the input is the same as before, must be some special key like enter etc.
    if (event.target.value === this.state.formattedNumber) {
      return;
    }

    // ie hack
    if (event.preventDefault) {
      event.preventDefault();
    } else {
      event.returnValue = false;
    }

    if (event.target.value.length > 0) {
      // before entering the number in new format,
      // lets check if the dial code now matches some other country
      const inputNumber = event.target.value.replace(/\D/g, '');

      // we don't need to send the whole number to guess the country... only the first 6 characters
      // are enough the guess country function can then use memoization much more effectively since
      // the set of input it gets has drastically reduced
      if (
        !this.state.freezeSelection ||
        this.state.selectedCountry.dialCode.length > inputNumber.length
      ) {
        newSelectedCountry = format.phone.bestGuessSelectedCountry(
          inputNumber.substring(0, 6),
          this.searchCountries,
          this.props.defaultCountry,
        );
        freezeSelection = false;
      }
      // let us remove all non numerals from the input
      formattedNumber = format.phone.formatNumber(inputNumber, newSelectedCountry);
    }

    let caretPosition = event.target.selectionStart;
    const oldFormattedText = this.state.formattedNumber;
    const diff = formattedNumber.length - oldFormattedText.length;
    this.setState(
      {
        formattedNumber,
        freezeSelection,
        selectedCountry:
          newSelectedCountry.dialCode.length > 0 ? newSelectedCountry : this.state.selectedCountry,
      },
      () => {
        if (utils.isModernBrowser) {
          if (caretPosition === 1 && formattedNumber.length === 2) {
            caretPosition += 1;
          }

          if (diff > 0) {
            caretPosition -= diff;
          }

          if (caretPosition > 0 && oldFormattedText.length >= formattedNumber.length) {
            this.phoneInputWrapper.setSelectionRange(caretPosition, caretPosition);
          }
        }

        if (this.props.onChange) {
          this.props.onChange(
            this.state.formattedNumber,
            this.state.selectedCountry.iso2,
            this.state.selectedCountry.dialCode,
          );
        }
      },
    );
  }

  handleInputBlur() {
    if (typeof this.props.onBlur === 'function') {
      this.props.onBlur(
        this.state.formattedNumber,
        this.state.selectedCountry.iso2,
        this.state.selectedCountry.dialCode,
      );
    }
  }

  handleInputClick() {
    this.setState({ showDropDown: false });
  }

  handleInputFocus() {
    // trigger parent component's onFocus handler
    if (typeof this.props.onFocus === 'function') {
      this.props.onFocus(
        this.state.formattedNumber,
        this.state.selectedCountry,
        this.state.selectedCountry.dialCode,
      );
    }

    this.fillDialCode();
  }

  handleInputKeyDown(event) {
    if (event.which === utils.KEYS.ENTER) {
      this.props.onEnterKeyPress(event);
    }
  }

  handleKeyDown(event) {
    if (!this.state.showDropDown) {
      return;
    }

    // ie hack
    if (event.preventDefault) {
      event.preventDefault();
    } else {
      event.returnValue = false;
    }

    const self = this;
    const moveHighlight = direction => {
      self.setState({ highlightCountryIndex: self.getHighlightCountryIndex(direction) }, () =>
        self.scrollTo(self.getElement(self.state.highlightCountryIndex), true),
      );
    };

    switch (event.which) {
      case utils.KEYS.DOWN:
        moveHighlight(1);
        break;
      case utils.KEYS.UP:
        moveHighlight(-1);
        break;
      case utils.KEYS.ENTER:
        this.handleFlagItemClick(this.optionCountries[this.state.highlightCountryIndex], event);
        break;
      case utils.KEYS.ESC:
        this.setState({ showDropDown: false }, this.cursorToEnd);
        break;
      default:
        if (
          (event.which >= utils.KEYS.A && event.which <= utils.KEYS.Z) ||
          event.which === utils.KEYS.SPACE
        ) {
          this.setState(
            { queryString: String.fromCharCode(event.which) },
            this.state.debouncedQueryStingSearcher,
          );
        }
    }
  }

  scrollTo(country, middle) {
    if (!country) {
      return;
    }

    const container = this.flagDropdownListWrapper;

    if (!container) {
      return;
    }

    const containerHeight = container.offsetHeight;
    const containerOffset = container.getBoundingClientRect();
    const containerTop = containerOffset.top + document.body.scrollTop;
    const containerBottom = containerTop + containerHeight;

    const element = country;
    const elementOffset = element.getBoundingClientRect();

    const elementHeight = element.offsetHeight;
    const elementTop = elementOffset.top + document.body.scrollTop;
    const elementBottom = elementTop + elementHeight;
    let newScrollTop = elementTop - containerTop + container.scrollTop;
    const middleOffset = containerHeight / 2 - elementHeight / 2;

    if (elementTop < containerTop) {
      // scroll up
      if (middle) {
        newScrollTop -= middleOffset;
      }
      container.scrollTop = newScrollTop;
    } else if (elementBottom > containerBottom) {
      // scroll down
      if (middle) {
        newScrollTop += middleOffset;
      }
      const heightDifference = containerHeight - elementHeight;
      container.scrollTop = newScrollTop - heightDifference;
    }
  }

  searchThroughCountries(queryString) {
    if (!queryString || queryString.length === 0) {
      return 0;
    }
    // don't include the preferred countries in search
    const index = _.findIndex(this.searchCountries, country =>
      _.startsWith(country.name.toLowerCase(), queryString.toLowerCase()),
    );
    return index > -1 ? index : 0;
  }

  searchCountry() {
    const probableCandidateIndex =
      this.searchThroughCountries(this.state.queryString) + this.preferredCountries.length;

    this.scrollTo(this.getElement(probableCandidateIndex), true);
    this.setState({
      queryString: '',
      highlightCountryIndex: probableCandidateIndex,
    });
  }

  render() {
    const arrowClasses = classnames({
      arrow: true,
      up: this.state.showDropDown,
    });
    const inputClasses = classnames({
      'form-control': true,
      'invalid-number': !format.phone.isNumberValid(this.state.formattedNumber.replace(/\D/g, '')),
    });

    const flagViewClasses = classnames({
      'flag-dropdown': true,
      'open-dropdown': this.state.showDropDown,
    });

    const inputFlagClasses = `flag ${this.state.selectedCountry.iso2}`;
    const otherProps = this.props.inputProps;
    if (this.props.inputId) {
      otherProps.id = this.props.inputId;
    }
    return (
      <div className={classnames('react-tel-input', 'f32', this.props.className)}>
        <input
          onChange={this.handleInput}
          onClick={this.handleInputClick}
          onFocus={this.handleInputFocus}
          onBlur={this.handleInputBlur}
          onKeyDown={this.handleInputKeyDown}
          value={this.state.formattedNumber}
          ref={wrapper => {
            this.phoneInputWrapper = wrapper;
          }}
          type="tel"
          className={inputClasses}
          autoComplete={this.props.autoComplete}
          pattern={this.props.pattern}
          required={this.props.required}
          placeholder={this.props.placeholder}
          disabled={this.props.disabled}
          {...otherProps}
        />
        <div
          className={flagViewClasses}
          onKeyDown={this.handleKeyDown}
          onClick={this.handleFlagDropdownClick}
          role="button"
          tabIndex={0}
        >
          <div
            className="selected-flag"
            title={`${this.state.selectedCountry.name}: + ${this.state.selectedCountry.dialCode}`}
          >
            <div className={inputFlagClasses}>
              <div className={arrowClasses} />
            </div>
          </div>
          {this.state.showDropDown ? this.getCountryDropDownList() : null}
        </div>
      </div>
    );
  }
}

ReactTelephoneInput.propTypes = {
  className: PropTypes.string,
  value: PropTypes.string,
  initialValue: PropTypes.string,
  autoComplete: PropTypes.string,
  defaultCountry: PropTypes.string,
  disabled: PropTypes.bool,
  onlyCountries: PropTypes.arrayOf(PropTypes.string),
  preferredCountries: PropTypes.arrayOf(PropTypes.string),
  inputId: PropTypes.string,
  onChange: PropTypes.func,
  onEnterKeyPress: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  placeholder: PropTypes.string,
  pattern: PropTypes.string,
  required: PropTypes.bool,
  inputProps: PropTypes.shape({}),
};

ReactTelephoneInput.defaultProps = {
  onlyCountries: _.keys(iso2Lookup),
  defaultCountry: allCountries[0].iso2,
  onEnterKeyPress: () => {},
  preferredCountries: [],
  disabled: false,
  placeholder: '+1 (702) 123-4567',
  autoComplete: 'tel',
  required: false,
  inputProps: {},
};

export default onClickOutside(ReactTelephoneInput);
